export const hasPermissions = (
  requiredPermissions = [],
  availablePermissions = []
) => {
  return requiredPermissions.some(permission =>
    availablePermissions.includes(permission)
  );
};

export const getCurrentAccount = ({ accounts } = {}, accountId = null) => {
  return accounts.find(account => Number(account.id) === Number(accountId));
};

export function accountUserHasPermission(user, accountId, key) {
  const currentAccount = getCurrentAccount(user, accountId) || {};

  if (user?.type === 'SuperAdmin') {
    return true;
  }

  // Custom role system only applies to agents with custom_role_id.
  // Everyone else should never be restricted by it.
  if (currentAccount.role === 'administrator') {
    return true;
  }

  if (currentAccount.role !== 'agent') {
    return true;
  }

  if (!currentAccount.custom_role_id) {
    return true;
  }

  const perms = currentAccount.custom_role?.permissions;
  if (!perms || typeof perms !== 'object') {
    return false;
  }

  return perms[key] === true;
}

export const getUserPermissions = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};

  if (user?.type === 'SuperAdmin') {
    return ['administrator', 'agent'];
  }

  if (currentAccount.role === 'administrator') {
    return ['administrator', 'agent'];
  }

  // Custom role system only applies to agents with custom_role_id.
  // Agent without custom role should behave like default Chatwoot agent.
  if (currentAccount.role !== 'agent') {
    return ['agent'];
  }

  if (
    currentAccount.custom_role_id &&
    currentAccount.custom_role?.permissions &&
    typeof currentAccount.custom_role.permissions === 'object'
  ) {
    const keys = Object.entries(currentAccount.custom_role.permissions)
      .filter(([, v]) => v === true)
      .map(([k]) => k);
    return [...keys, 'custom_role'];
  }

  return ['agent'];
};

export const getUserRole = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};
  if (user?.type === 'SuperAdmin') {
    return 'administrator';
  }

  if (currentAccount.role === 'administrator') {
    return 'administrator';
  }

  if (currentAccount.role === 'agent' && currentAccount.custom_role_id) {
    return 'custom_role';
  }

  return currentAccount.role || 'agent';
};

/**
 * Filters and transforms items based on user permissions.
 *
 * @param {Object} items - An object containing items to be filtered.
 * @param {Array} userPermissions - Array of permissions the user has.
 * @param {Function} getPermissions - Function to extract required permissions from an item.
 * @param {Function} [transformItem] - Optional function to transform each item after filtering.
 * @returns {Array} Filtered and transformed items.
 */
export const filterItemsByPermission = (
  items,
  userPermissions,
  getPermissions,
  transformItem = (key, item) => ({ key, ...item })
) => {
  // Helper function to check if an item has the required permissions
  const hasRequiredPermissions = item => {
    const requiredPermissions = getPermissions(item);
    return (
      requiredPermissions.length === 0 ||
      hasPermissions(requiredPermissions, userPermissions)
    );
  };

  return Object.entries(items)
    .filter(([, item]) => hasRequiredPermissions(item)) // Keep only items with required permissions
    .map(([key, item]) => transformItem(key, item)); // Transform each remaining item
};
